import React, { useMemo, useState } from 'react';
import { Plus, Landmark, Trash2, Pencil, X } from 'lucide-react';
import { PageSearchBar } from '../common/PageSearchBar';
import { MobileModalPortal } from '../common/MobileModalPortal';
import {
  readCompanyBanks,
  writeCompanyBanks,
} from '../../lib/financeCatalogs';
import type { CompanyBankAccount } from '../../lib/paymentVia';
import { useTranslation } from '../../lib/i18n';

function emptyBank(): CompanyBankAccount {
  return {
    id: crypto.randomUUID(),
    bankName: '',
    accountName: '',
    accountNumber: '',
    ifsc: '',
    branch: '',
  };
}

const BanksSettings: React.FC = () => {
  const { t } = useTranslation();
  const [banks, setBanks] = useState<CompanyBankAccount[]>(() => readCompanyBanks());
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<CompanyBankAccount | null>(null);
  const [showForm, setShowForm] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return banks;
    return banks.filter((b) =>
      [b.bankName, b.accountName, b.accountNumber, b.ifsc, b.branch].join(' ').toLowerCase().includes(q)
    );
  }, [banks, search]);

  const persist = (next: CompanyBankAccount[]) => {
    setBanks(next);
    writeCompanyBanks(next);
  };

  const openNew = () => {
    setEditing(emptyBank());
    setShowForm(true);
  };

  const openEdit = (bank: CompanyBankAccount) => {
    setEditing({ ...bank });
    setShowForm(true);
  };

  const saveBank = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    if (!editing.bankName.trim()) return;
    const row = {
      ...editing,
      bankName: editing.bankName.trim(),
      accountName: editing.accountName.trim(),
      accountNumber: editing.accountNumber.trim(),
      ifsc: editing.ifsc.trim().toUpperCase(),
      branch: editing.branch.trim(),
    };
    const exists = banks.some((b) => b.id === row.id);
    persist(exists ? banks.map((b) => (b.id === row.id ? row : b)) : [row, ...banks]);
    setEditing(null);
    setShowForm(false);
  };

  const remove = (id: string) => {
    if (!window.confirm(t('settingsForm.confirmDeleteBank'))) return;
    persist(banks.filter((b) => b.id !== id));
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 text-zinc-900">
          <Landmark className="h-5 w-5 shrink-0 text-emerald-700" aria-hidden />
          <p className="text-sm text-zinc-600">{t('settings.banksDesc')}</p>
        </div>
        <button
          type="button"
          onClick={openNew}
          className="app-btn-primary inline-flex w-full items-center justify-center gap-2 text-sm sm:w-auto"
        >
          <Plus className="h-4 w-4" />
          {t('settingsForm.addBank')}
        </button>
      </div>

      <PageSearchBar value={search} onChange={setSearch} placeholder={t('common.search')} className="max-w-full" />

      {banks.length > 0 ? (
        <p className="text-xs font-medium tabular-nums text-zinc-500">
          {filtered.length} of {banks.length} account{banks.length === 1 ? '' : 's'}
        </p>
      ) : null}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {filtered.map((bank) => (
          <article
            key={bank.id}
            className="overflow-hidden rounded-2xl border border-zinc-200/90 bg-white shadow-sm ring-1 ring-zinc-950/[0.04] transition hover:border-sky-200/80 hover:shadow-md"
          >
            <div className="flex gap-3 border-l-[3px] border-l-sky-600 p-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-sky-50 to-emerald-50 text-sky-700 ring-1 ring-sky-100">
                <Landmark className="h-5 w-5" aria-hidden />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="truncate text-base font-semibold leading-snug text-zinc-900">
                  {bank.bankName || '—'}
                </h3>
                <dl className="mt-2 space-y-1">
                  <div className="text-sm leading-snug text-zinc-800">
                    <dt className="inline text-xs font-medium text-zinc-500">Name:</dt>
                    <dd className="inline font-medium"> {bank.accountName || '—'}</dd>
                  </div>
                  <div className="text-sm leading-snug text-zinc-800">
                    <dt className="inline text-xs font-medium text-zinc-500">Ac No:</dt>
                    <dd className="inline font-mono text-xs font-semibold tabular-nums"> {bank.accountNumber || '—'}</dd>
                  </div>
                </dl>
              </div>
            </div>
            <div className="grid grid-cols-2 divide-x divide-zinc-100 border-t border-zinc-100 bg-zinc-50/60">
              <button
                type="button"
                onClick={() => openEdit(bank)}
                className="inline-flex min-h-[2.75rem] items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-semibold text-zinc-700 transition hover:bg-white active:bg-zinc-100"
              >
                <Pencil className="h-3.5 w-3.5 shrink-0" aria-hidden />
                {t('common.edit')}
              </button>
              <button
                type="button"
                onClick={() => remove(bank.id)}
                className="inline-flex min-h-[2.75rem] items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-semibold text-rose-700 transition hover:bg-rose-50/80 active:bg-rose-100/60"
              >
                <Trash2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                {t('common.delete')}
              </button>
            </div>
          </article>
        ))}
        {banks.length === 0 && (
          <div className="col-span-full rounded-2xl border border-dashed border-zinc-300 bg-gradient-to-b from-zinc-50 to-white px-6 py-12 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-sky-50 text-sky-600 ring-1 ring-sky-100">
              <Landmark className="h-6 w-6" aria-hidden />
            </div>
            <p className="mt-3 text-sm font-medium text-zinc-700">{t('settingsForm.noBanksYet')}</p>
            <button type="button" onClick={openNew} className="app-btn-primary mt-4 inline-flex items-center gap-2 text-sm">
              <Plus className="h-4 w-4" />
              {t('settingsForm.addBank')}
            </button>
          </div>
        )}
        {banks.length > 0 && filtered.length === 0 && (
          <p className="col-span-full rounded-xl border border-dashed border-zinc-200 bg-zinc-50/80 py-8 text-center text-sm text-zinc-500">
            No banks match your search.
          </p>
        )}
      </div>

      {showForm && editing && (
        <MobileModalPortal
          onClose={() => {
            setShowForm(false);
            setEditing(null);
          }}
        >
          <div className="flex max-h-[min(92dvh,calc(100dvh-env(safe-area-inset-bottom,0px)))] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl border border-zinc-200 bg-white shadow-2xl sm:max-h-[90vh] sm:rounded-2xl">
            <div className="flex shrink-0 items-center justify-between border-b border-zinc-200 px-4 py-3">
              <h3 className="pr-2 text-sm font-semibold text-zinc-900">
                {banks.some((b) => b.id === editing.id) ? t('common.edit') : t('common.add')} {t('settings.banks')}
              </h3>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditing(null);
                }}
                className="rounded-md p-1 text-zinc-500 hover:bg-zinc-100"
                aria-label={t('common.close')}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={saveBank} className="flex min-h-0 flex-1 flex-col">
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
                <div>
                  <label className="app-label">{t('settingsForm.bankName')}</label>
                  <input
                    className="app-field mt-1"
                    value={editing.bankName}
                    onChange={(e) => setEditing({ ...editing, bankName: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="app-label">{t('settingsForm.branch')}</label>
                  <input
                    className="app-field mt-1"
                    value={editing.branch}
                    onChange={(e) => setEditing({ ...editing, branch: e.target.value })}
                    placeholder={t('settingsForm.branchPlaceholder')}
                  />
                </div>
                <div>
                  <label className="app-label">{t('settingsForm.accountName')}</label>
                  <input
                    className="app-field mt-1"
                    value={editing.accountName}
                    onChange={(e) => setEditing({ ...editing, accountName: e.target.value })}
                  />
                </div>
                <div>
                  <label className="app-label">{t('settingsForm.accountNumber')}</label>
                  <input
                    className="app-field mt-1 font-mono"
                    value={editing.accountNumber}
                    onChange={(e) => setEditing({ ...editing, accountNumber: e.target.value })}
                  />
                </div>
                <div>
                  <label className="app-label">IFSC</label>
                  <input
                    className="app-field mt-1 font-mono uppercase"
                    value={editing.ifsc}
                    onChange={(e) => setEditing({ ...editing, ifsc: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex shrink-0 justify-end gap-2 border-t border-zinc-200 bg-white px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))]">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditing(null);
                  }}
                  className="app-btn-secondary"
                >
                  {t('common.cancel')}
                </button>
                <button type="submit" className="app-btn-primary">
                  {t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </MobileModalPortal>
      )}
    </div>
  );
};

export default BanksSettings;
